import React, { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Navigate, useLocation } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SquaresFour, Envelope, Lock, User, GoogleLogo } from '@phosphor-icons/react';

export default function LoginPage() {
  const { user, loading, login, register } = useAuth();
  const location = useLocation();
  const [showEmailAuth, setShowEmailAuth] = useState(false);
  const [isRegister, setIsRegister] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState(location.state?.error || '');
  const [submitting, setSubmitting] = useState(false);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-white">
      <div className="animate-pulse text-[#555555]">Loading...</div>
    </div>
  );
  if (user) return <Navigate to="/" replace />;

  const handleGoogleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    const result = isRegister
      ? await register(email, password, name)
      : await login(email, password);
    if (!result.success) setError(result.error);
    setSubmitting(false);
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left panel */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#0F0F0F] flex-col justify-between p-12">
        <div className="flex items-center gap-3">
          <SquaresFour size={28} weight="bold" className="text-[#32B4AC]" />
          <span className="text-white font-['Manrope'] text-xl font-bold tracking-tight">TechFlow</span>
        </div>
        <div className="max-w-md">
          <h1 className="text-white font-['Manrope'] text-4xl font-extrabold tracking-tighter leading-tight mb-4">
            Centralized task management for D2C teams
          </h1>
          <p className="text-[#E8E8E8] text-sm leading-relaxed">
            Align internal, tech, and design stakeholders. Track ownership, status, and progress across all brands in one place.
          </p>
        </div>
        <p className="text-[#555555] text-xs">Built for cross-team visibility</p>
      </div>

      {/* Right panel */}
      <div className="flex-1 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-3 mb-10">
            <SquaresFour size={28} weight="bold" className="text-[#32B4AC]" />
            <span className="text-[#0F0F0F] font-['Manrope'] text-xl font-bold">TechFlow</span>
          </div>

          <h2 className="font-['Manrope'] text-2xl font-bold tracking-tight text-[#0F0F0F] mb-1" data-testid="login-title">
            Log Into TechFlow
          </h2>
          <p className="text-sm text-[#555555] mb-8">
            Use your @wellversed.in Google account to continue
          </p>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-md mb-6" data-testid="auth-error">
              {error}
            </div>
          )}

          {/* Google Sign-In — Single primary action */}
          <button
            data-testid="google-signin-btn"
            onClick={handleGoogleSignIn}
            className="w-full h-12 flex items-center justify-center gap-3 bg-[#0F0F0F] text-white text-sm font-semibold rounded-md hover:bg-[#1A1A1A] transition-colors duration-150"
          >
            <GoogleLogo size={20} weight="bold" />
            Continue with Google
          </button>

          <p className="text-xs text-[#555555] text-center mt-3">
            Restricted to <span className="font-semibold text-[#0F0F0F]">@wellversed.in</span> emails
          </p>

          {/* Divider + email fallback (collapsed by default) */}
          <div className="flex items-center gap-3 mt-8 mb-4">
            <div className="flex-1 h-px bg-[#E8E8E8]" />
            <button
              onClick={() => setShowEmailAuth(!showEmailAuth)}
              className="text-xs text-[#555555] hover:text-[#0F0F0F] transition-colors"
              data-testid="toggle-email-auth-btn"
            >
              {showEmailAuth ? 'Hide' : 'Staff login'}
            </button>
            <div className="flex-1 h-px bg-[#E8E8E8]" />
          </div>

          {showEmailAuth && (
            <form onSubmit={handleSubmit} className="space-y-4">
              {isRegister && (
                <div>
                  <Label className="text-xs font-medium text-[#1A1A1A] uppercase tracking-wider mb-1.5 block">Name</Label>
                  <div className="relative">
                    <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#555555]" />
                    <Input data-testid="register-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your full name" className="pl-9 border-[#E8E8E8] rounded-md h-10 text-sm" required />
                  </div>
                </div>
              )}
              <div>
                <Label className="text-xs font-medium text-[#1A1A1A] uppercase tracking-wider mb-1.5 block">Email</Label>
                <div className="relative">
                  <Envelope size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#555555]" />
                  <Input data-testid="login-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="admin@taskflow.com" className="pl-9 border-[#E8E8E8] rounded-md h-10 text-sm" required />
                </div>
              </div>
              <div>
                <Label className="text-xs font-medium text-[#1A1A1A] uppercase tracking-wider mb-1.5 block">Password</Label>
                <div className="relative">
                  <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#555555]" />
                  <Input data-testid="login-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" className="pl-9 border-[#E8E8E8] rounded-md h-10 text-sm" required minLength={6} />
                </div>
              </div>
              <button data-testid="login-submit-btn" type="submit" disabled={submitting} className="w-full h-10 bg-[#32B4AC] text-white text-sm font-semibold rounded-md hover:bg-[#2A9B94] transition-colors disabled:opacity-50">
                {submitting ? 'Please wait...' : (isRegister ? 'Create account' : 'Sign in')}
              </button>
              <p className="text-center text-xs text-[#555555]">
                {isRegister ? 'Have an account?' : "Need an account?"}{' '}
                <button type="button" data-testid="toggle-auth-mode-btn" onClick={() => { setIsRegister(!isRegister); setError(''); }} className="text-[#32B4AC] font-medium hover:underline">
                  {isRegister ? 'Sign in' : 'Register'}
                </button>
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
